const instances = new Map();
const views     = new Map();

// const eventTypes = Object.keys(window).filter(key => /^on/.test(key)).map(key => key.slice(2));

export default class View {
    /**
     * @var {Map}
     */
    #handlers = new Map();

    /**
     * The HTML element this View is attached to.
     *
     * @param {HTMLElement} $el 
     */
    constructor($el) {
        this.$el = $el;
        this.$el.__view = this;

        // // Programmatically set "onX" methods for all possible even types (click, keydown...)
        // eventTypes.forEach(eventType => {
        //     this['on' + (eventType.charAt(0).toUpperCase() + eventType.slice(1))] = (e) => this.getEventHandlers(eventType).forEach(handler => handler(e));
        // });
    }

    /**
     * @returns {Map}
     */
    static get instances() {
        return instances;
    }

    /**
     * @returns {Map}
     */
    static get views() {
        return views;
    }

    /**
     * @param {string|NodeList} selector
     * @param {array} views
     */
    static initInstances(selector = '[data-view-name]', views = []) {
        View.startInstances(View.setInstances(selector, views));
    }

    /**
     * @param {string|NodeList} selector
     * @param {array} views
     * @returns {array}
     */
    static setInstances(selector = '[data-view-name]', views = []) {
        if (views.length) {
            views.forEach(view => View.views.set(`${view.viewType}:${view.viewName}`, view));
        }

        views = Array.from(View.views.values());

        if (!views.length) {
            return;
        }

        const elements = typeof selector === 'string' ? document.querySelectorAll(selector) : selector;

        return Array.from(elements).map(el => {
            const { viewName, viewType, viewId } = el.dataset || {};

            if (!viewName || !viewType || !viewId) {
                return;
            }

            const view = views.filter(view => (view.viewType === viewType) && (view.viewName === viewName))[0];

            if (!view) {
                return;
            }

            const instance = new view(el);

            View.instances.set(viewId, instance);

            return instance;
        }).filter(instance => !!instance);
    }

    /**
     * @param {array|null} instances
     */
    static startInstances(instances = null) {
        if (!instances) {
            View.instances.forEach(instance => instance.start());

            return;
        }

        instances.forEach(instance => instance.start());
    }

    /**
     * @param {string|NodeList} selector
     */
    static destroyInstances(selector = '[data-view-name]') {
        const elements = typeof selector === 'string' ? document.querySelectorAll(selector) : selector;

        elements.forEach(el => {
            const { viewId } = el.dataset || {};

            if (!viewId) {
                return;
            }

            const instance = View.instances.get(viewId);

            if (!instance) {
                return;
            }

            instance.destroy();

            View.instances.delete(viewId);
        });
    }

    /**
     * @param {string|NodeList} selector
     */
    static refreshInstances(selector = '[data-view-name]') {
        View.destroyInstances(selector);
        View.initInstances(selector, Array.from(View.views.values()));
    }

    start() {
        //
    }

    destroy() {
        //
    }

    /**
     * @returns {DOMStringMap}
     */
    get dataset() {
        return this.$el.dataset || {};
    }

    /**
     * @returns {string}
     */
    get viewId() {
        return this.dataset.viewId;
    }

    /**
     * @returns {Object|null}
     */
    get viewData() {
        let data = null;

        if (!this.dataset.viewData) {
            return data;
        }

        try {
            data = JSON.parse(this.dataset.viewData);
        } catch (error) {
            return data;
        }

        if (!Object.keys(data ?? {}).length) {
            data = null;
        }

        return data;
    }

    /**
     * @returns {Boolean}
     */
    get isLegacyBrowser() {
        return document.querySelector('html').className.indexOf('is-legacy-browser') !== -1;
    }

    /**
     * @param {HTMLElement} el
     * @returns {Boolean}
     */
    isSameNode(el) {
        return el.isSameNode(this.$el);
    }

    /**
     * @param {Event} e
     * @returns {Boolean}
     */
    isEventTarget(e) {
        return (e.delegateTarget || e.currentTarget)?.isSameNode(this.$el);
    }

    /**
     * @param {string} eventType
     * @param {function} handler
     */
    addEventHandler(eventType, handler) {
        if (!(eventType || '').trim() || (typeof handler !== 'function')) {
            return;
        }

        if (!this.#handlers.has(this.viewId)) {
            this.#handlers.set(this.viewId, new Map());
        }

        if (!this.#handlers.get(this.viewId).has(eventType)) {
            this.#handlers.get(this.viewId).set(eventType, new Set());
        }

        this.#handlers.get(this.viewId).get(eventType).add(handler);
    }

    /**
     * @param {Event} e
     */
    triggerEventHandlers(e) {
        this.#handlers.get((e.delegateTarget || e.currentTarget)?.dataset?.viewId)?.get(e.type)?.forEach(handler => handler(e));
    }

    /**
     * @param {CustomEvent} e
     */
    dispatchEvent(e) {
        this.#handlers?.forEach(viewIds => {
            viewIds?.get(e.type)?.forEach(handler => handler(e));
        });
    }
}